/**
 * PATH: packages/pricelist/src/HiddenColumnsTab.tsx
 * FILE_ID: PKG-PRICELIST-HIDDENCOLS-001
 * PURPOSE: "Hidden columns" tab of the column manager — lists every column
 *          currently flagged hidden_by_default (except harvested/IQ-gated
 *          ones), lets the user tick some and click Show to unhide them.
 * WHY: 2026-07-14 meeting task 13 — visible columns could be hidden but there
 *      was no way back. Saving still goes through the manager's Save button,
 *      so the change applies uniformly to all sections (org default).
 * RELATED: ColumnManagerDialog.tsx (host), iqSchema.ts (gated set)
 */
import { useState } from 'react'
import { Eye } from 'lucide-react'
import type { ColumnDef } from './types'
import { IQ_GATED_COLUMN_KEYS } from './iqSchema'

export interface HiddenColumnsTabProps {
  columns: ColumnDef[]
  /** Unhide the given keys (sets hidden_by_default=false on each). */
  onShow: (keys: string[]) => void
  /** Read-only: list hidden columns but hide the select/Show controls (viewers
   *  who may see every column but not persist an org-wide unhide). */
  viewOnly?: boolean
}

export default function HiddenColumnsTab({ columns, onShow, viewOnly = false }: HiddenColumnsTabProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // Harvested/IQ-gated columns are managed by the IQ policy, not this tab.
  const hidden = columns.filter(
    (col) => col.hidden_by_default === true && !IQ_GATED_COLUMN_KEYS.has(col.key),
  )

  if (hidden.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-slate-400">
        No hidden columns. Tick “Hidden” on a column in the Columns tab to hide it.
      </p>
    )
  }

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        {viewOnly
          ? 'These columns are hidden by the org default view. Use “Show hidden columns” above the grid to reveal them in your own view.'
          : 'Select hidden columns and click Show, then Save to apply to every section.'}
      </p>
      {hidden.map((col) => (
        <label
          key={col.key}
          className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm text-slate-800 dark:border-slate-700 dark:text-slate-100"
        >
          {!viewOnly && (
            <input
              type="checkbox"
              checked={selected.has(col.key)}
              onChange={() => toggle(col.key)}
              aria-label={`Select ${col.label || col.key}`}
            />
          )}
          <span className="flex-1">{col.label || col.key}</span>
          <code className="text-[11px] text-slate-400">{col.key}</code>
        </label>
      ))}
      {!viewOnly && (
        <button
          type="button"
          disabled={selected.size === 0}
          onClick={() => {
            onShow([...selected])
            setSelected(new Set())
          }}
          className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          <Eye className="h-4 w-4" /> Show selected
        </button>
      )}
    </div>
  )
}
